import { useEffect, useMemo, useState } from "react";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import BulkActionBar from "@/components/BulkActionBar";
import useBulkSelection from "@/hooks/useBulkSelection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter, X, CheckCircle2, Ban, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    status: "",       // "" | "active" | "inactive" | "deleted"
    has_orders: "",   // "" | "yes" | "no"
    joined_from: "",
    joined_to: "",
    ltv_min: "",
  });

  const sel = useBulkSelection();

  const filterCount = useMemo(
    () => Object.values(filters).filter((v) => v !== "" && v !== null).length,
    [filters]
  );

  const fetchRows = async (pg = page, query = q, f = filters) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: pg, limit });
      if (query) qs.set("q", query);
      if (f.status) qs.set("status", f.status);
      const { data } = await api.get(`/customers?${qs}`);
      let items = data.items || [];
      // Client-side filters (backend doesn't support these yet)
      if (f.has_orders === "yes") items = items.filter((r) => (r.orders_count || 0) > 0);
      else if (f.has_orders === "no") items = items.filter((r) => (r.orders_count || 0) === 0);
      if (f.joined_from) {
        const t = new Date(f.joined_from).getTime();
        items = items.filter((r) => new Date(r.created_at).getTime() >= t);
      }
      if (f.joined_to) {
        const t = new Date(f.joined_to).getTime() + 24 * 3600 * 1000;
        items = items.filter((r) => new Date(r.created_at).getTime() <= t);
      }
      if (f.ltv_min) {
        const min = Number(f.ltv_min);
        items = items.filter((r) => Number(r.lifetime_value || 0) >= min);
      }
      setRows(items);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load customers"));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRows(page, q, filters); /* eslint-disable-next-line */ }, [page]);

  // Bulk actions
  const runBulk = async (fn, verb, past) => {
    const ids = sel.ids;
    if (ids.length === 0) return;
    if (verb === "delete" && !window.confirm(`Delete ${ids.length} selected customer${ids.length > 1 ? "s" : ""}? Underlying user accounts stay intact but the profile is marked deleted.`)) return;
    const t = toast.loading(`${verb === "delete" ? "Deleting" : verb === "activate" ? "Activating" : "Deactivating"} ${ids.length}…`);
    const results = await Promise.allSettled(ids.map(fn));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = ids.length - ok;
    toast.dismiss(t);
    if (fail === 0) toast.success(`${past} ${ok}`);
    else toast.warning(`${ok} ok · ${fail} failed`);
    sel.clear();
    fetchRows(page, q, filters);
  };
  const bulkActivate   = () => runBulk((id) => api.patch(`/customers/${id}`, { status: "active" }),   "activate",   "Activated");
  const bulkDeactivate = () => runBulk((id) => api.patch(`/customers/${id}`, { status: "inactive" }), "deactivate", "Deactivated");
  const bulkDelete     = () => runBulk((id) => api.delete(`/customers/${id}`),                        "delete",     "Deleted");

  const columns = [
    { key: "name", header: "Customer", render: (r) => (
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent text-white grid place-items-center text-xs font-semibold">
          {(r.name || r.email || "U").slice(0, 1).toUpperCase()}
        </div>
        <div>
          <div className="font-medium">{r.name || "—"}</div>
          <div className="text-xs text-muted-foreground">{r.email}</div>
        </div>
      </div>
    )},
    { key: "phone", header: "Phone", render: (r) => r.phone || "—" },
    { key: "orders_count", header: "Orders", className: "w-24 text-right", render: (r) => r.orders_count ?? 0 },
    { key: "lifetime_value", header: "LTV", className: "w-32 text-right", render: (r) => <span className="font-semibold">{money(r.lifetime_value)}</span> },
    { key: "status", header: "Status", className: "w-24",
      render: (r) => <Badge variant={r.status === "active" ? "default" : "secondary"} className={r.status === "active" ? "bg-accent hover:bg-accent" : ""}>{r.status}</Badge> },
    { key: "created_at", header: "Joined", render: (r) => <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span> },
  ];

  return (
    <div className="p-4 md:p-8" data-testid="customers-page">
      <PageHeader
        title="Customers"
        subtitle="People who shop with you"
        actions={
          <Button
            variant={filtersOpen || filterCount > 0 ? "default" : "outline"}
            onClick={() => setFiltersOpen((v) => !v)}
            data-testid="customers-filter-toggle"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters{filterCount > 0 ? ` · ${filterCount}` : ""}
          </Button>
        }
      />

      {filtersOpen && (
        <div className="mb-4 rounded-2xl border bg-card p-4 space-y-3" data-testid="customers-filter-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-[11px]">Status</Label>
              <Select value={filters.status || "__any"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "__any" ? "" : v }))}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="deleted">Deleted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Has orders</Label>
              <Select value={filters.has_orders || "__any"} onValueChange={(v) => setFilters((f) => ({ ...f, has_orders: v === "__any" ? "" : v }))}>
                <SelectTrigger data-testid="filter-hasorders"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  <SelectItem value="yes">1+ orders</SelectItem>
                  <SelectItem value="no">No orders</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Joined from</Label>
              <Input type="date" value={filters.joined_from} onChange={(e) => setFilters((f) => ({ ...f, joined_from: e.target.value }))} data-testid="filter-joined-from" />
            </div>
            <div>
              <Label className="text-[11px]">Joined to</Label>
              <Input type="date" value={filters.joined_to} onChange={(e) => setFilters((f) => ({ ...f, joined_to: e.target.value }))} data-testid="filter-joined-to" />
            </div>
            <div>
              <Label className="text-[11px]">Min LTV (₹)</Label>
              <Input type="number" placeholder="0" value={filters.ltv_min} onChange={(e) => setFilters((f) => ({ ...f, ltv_min: e.target.value }))} data-testid="filter-ltv-min" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="btn-primary-glow" onClick={() => { setPage(1); fetchRows(1, q, filters); }} data-testid="filter-apply">
              Apply filters
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              const clean = { status: "", has_orders: "", joined_from: "", joined_to: "", ltv_min: "" };
              setFilters(clean); setPage(1); fetchRows(1, q, clean);
            }} data-testid="filter-clear">
              <X className="w-3.5 h-3.5 mr-1" />Clear all
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns} rows={rows} loading={loading}
        onSearch={(v) => { setQ(v); setPage(1); fetchRows(1, v, filters); }}
        searchPlaceholder="Search by name / email / phone…"
        page={page} limit={limit} total={total} onPage={setPage}
        selection={sel}
        testId="customers-table"
      />

      <BulkActionBar
        count={sel.count}
        onClear={sel.clear}
        actions={[
          { label: "Activate",   icon: <CheckCircle2 className="w-3.5 h-3.5" />, onClick: bulkActivate,   testId: "bulk-activate" },
          { label: "Deactivate", icon: <Ban className="w-3.5 h-3.5" />,          onClick: bulkDeactivate, testId: "bulk-deactivate" },
          { label: "Delete",     icon: <Trash2 className="w-3.5 h-3.5" />,       onClick: bulkDelete, tone: "destructive", testId: "bulk-delete" },
        ]}
      />
    </div>
  );
}
